/**
 * Homepage A/B Test Harness
 *
 * Reusable skeleton for running cookie-based homepage A/B tests. Currently
 * idle (TEST_ACTIVE = false) — the V1-B redesign is now baked into Liquid
 * so this file does nothing on page load until the next test ships.
 *
 * ─── To launch the next test ──────────────────────────────────────────
 *   1. Bump the IDs below (COOKIE_NAME, LS_KEY, CART_ATTR, GA4_TEST) to
 *      something unique for the new test, e.g. `ab_homepage_v3`.
 *   2. Flip TEST_ACTIVE to true.
 *   3. Fill variantBMods with DOM mutations that turn the baseline (what
 *      Liquid renders) into variant B. Variant A is the baseline — leave
 *      it untouched.
 *   4. If variant B needs styles, add `data-css-url="{{ 'ab-test-homepage-vX.css' | asset_url }}"`
 *      to the script tag in sections/homepage-image-grid.liquid. loadCSS()
 *      will pick it up automatically.
 *   5. If you need to cloak something during assignment (to prevent a flash
 *      of the wrong variant), add the cloak CSS to homepage-image-grid.liquid
 *      gated on an `html.ab-hp-vX-active` / `html.ab-hp-vX-ready` pair, and
 *      have applyMods() add the `-ready` class after reveal.
 *
 * ─── To end a test ────────────────────────────────────────────────────
 *   Flip TEST_ACTIVE back to false. Leave the IDs in place for analytics
 *   continuity if you want them queryable; bump them when starting a brand
 *   new test so cookies don't collide.
 */

(function () {
  'use strict';

  /* ========================================================================
     Test identity — bump per test
     ======================================================================== */
  var COOKIE_NAME  = 'ab_homepage_v3';
  var COOKIE_DAYS  = 30;
  var LS_KEY       = 'ab_homepage_v3';
  var CART_ATTR    = 'ab_homepage_test_v3';
  var GA4_EVENT    = 'ab_test_assignment';
  var GA4_TEST     = 'homepage_v3';

  /* ========================================================================
     Test status — flip to `true` to enable variant assignment.
     When false: no cookies set, no cart attrs, no GA4/Klaviyo events,
     no variant mods applied. Script is a no-op.
     ======================================================================== */
  var TEST_ACTIVE = false;

  /* ========================================================================
     Cookie helpers — reuse ORW utilities if available
     ======================================================================== */
  var getCookie = (typeof ORW !== 'undefined' && ORW.getCookie)
    ? ORW.getCookie
    : function (name) {
        var nameEQ = name + '=';
        var parts = document.cookie.split(';');
        for (var i = 0; i < parts.length; i++) {
          var trimmed = parts[i].trim();
          if (trimmed.indexOf(nameEQ) === 0) return trimmed.substring(nameEQ.length);
        }
        return null;
      };

  var setCookie = (typeof ORW !== 'undefined' && ORW.setCookie)
    ? ORW.setCookie
    : function (name, value, days) {
        var expires = '';
        if (days) {
          var date = new Date();
          date.setTime(date.getTime() + days * 86400000);
          expires = '; expires=' + date.toUTCString();
        }
        document.cookie = name + '=' + value + expires + '; path=/';
      };

  /* ========================================================================
     Variant assignment
     ======================================================================== */
  function getVariant() {
    // Debug override via URL param (not persisted)
    var params = new URLSearchParams(window.location.search);
    var override = params.get(COOKIE_NAME);
    if (override === 'A' || override === 'B') return override;

    // Check existing cookie
    var stored = getCookie(COOKIE_NAME);
    if (stored === 'A' || stored === 'B') return stored;

    // Check localStorage fallback (cookie may have been cleared)
    try {
      var lsStored = localStorage.getItem(LS_KEY);
      if (lsStored === 'A' || lsStored === 'B') {
        setCookie(COOKIE_NAME, lsStored, COOKIE_DAYS); // restore cookie
        return lsStored;
      }
    } catch (e) { /* localStorage unavailable */ }

    // First visit — random assignment
    var variant = Math.random() < 0.5 ? 'A' : 'B';
    setCookie(COOKIE_NAME, variant, COOKIE_DAYS);
    try { localStorage.setItem(LS_KEY, variant); } catch (e) {}
    return variant;
  }

  /* ========================================================================
     Cart attribute stamping — merges with any existing attrs
     ======================================================================== */
  function stampCart(variant) {
    var attrs = {};
    attrs[CART_ATTR] = variant;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    });
  }

  /* ========================================================================
     Analytics
     ======================================================================== */
  function trackAssignment(variant) {
    // 1. Shopify cart attribute — attempt now, retry on add-to-cart
    stampCart(variant).catch(function () { /* will retry on add-to-cart */ });

    // Re-stamp on every add-to-cart (cart guaranteed to exist at this point)
    if (typeof jQuery !== 'undefined') {
      jQuery(document.body).on('afterAddItem.ajaxCart', function () {
        stampCart(variant).catch(function () {});
      });
    }

    // 2. GA4 via GTM dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: GA4_EVENT,
      ab_test_name: GA4_TEST,
      ab_test_variant: variant
    });

    // 3. Klaviyo profile property
    window._learnq = window._learnq || [];
    window._learnq.push(['identify', {
      $ab_homepage: variant
    }]);
  }

  /* ========================================================================
     CSS loader — script tag may carry `data-css-url` for variant styles
     ======================================================================== */
  function loadCSS() {
    var scriptTag = document.querySelector('script[data-css-url]');
    if (!scriptTag) return;
    var href = scriptTag.getAttribute('data-css-url');
    if (!href) return;
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }

  /* ========================================================================
     Variant mods
     Fill `variantBMods` with functions that mutate the Liquid-rendered
     baseline into variant B. Variant A IS the baseline — leave it alone.
     Both run on DOMContentLoaded (or immediately if document is ready).
     ======================================================================== */
  var variantBMods = [];

  // Example skeleton — uncomment + customize when starting a test:
  //
  // variantBMods.push(function () {
  //   var hero = document.querySelector('[data-section-type="homepage-image-grid"] .image-grid__item:first-child img');
  //   if (!hero) return;
  //   var newSrc = 'https://cdn.shopify.com/.../variant-b-hero.jpg';
  //   hero.src = newSrc;
  //   hero.srcset = newSrc + ' 600w, ' + newSrc + ' 900w, ' + newSrc + ' 1200w';
  // });

  /* ========================================================================
     Wire up
     ======================================================================== */
  if (!TEST_ACTIVE) return;

  var variant = getVariant();
  trackAssignment(variant);
  loadCSS();

  function applyMods() {
    if (variant !== 'B') return;
    for (var i = 0; i < variantBMods.length; i++) {
      variantBMods[i]();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyMods);
  } else {
    applyMods();
  }
})();
